import { Plus, Wallet } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import PageContainer from "shared/components/pagecontainer/PageContainer";
import { CdCartao } from "pages/cadastrarmetodo/CdCartao";
import { MetodoItem } from "shared/components/metodoitem/MetodoItem";

import { ICartaoView } from "shared/interfaces/IMetodo";

import {
  buscarCartoes,
  excluirCartao,
  definirCartaoPadrao,
} from "shared/services/api/cartao.api";

import styles from "./MetodoPagamento.module.scss";

export const MetodoPagamento: React.FC = () => {
  const [openForm, setOpenForm] = useState(false);

  const [cartoes, setCartoes] = useState<ICartaoView[]>([]);

  const [loading, setLoading] = useState(true);

  const [cartaoSelecionado, setCartaoSelecionado] =
    useState<string | null>(null);

  const navigate = useNavigate();

  const location = useLocation();

  const returnTo = location.state?.returnTo;

  /* =========================================
     CARREGAR CARTÕES
  ========================================= */

  const carregarCartao = async () => {
    try {
      setLoading(true);

      const cartoesSalvos = await buscarCartoes();

      setCartoes(cartoesSalvos);

      const principal = cartoesSalvos.find(
        (cartao) => cartao.defaultCard
      );

      if (principal) {
        setCartaoSelecionado(principal.id);
      } else if (cartoesSalvos.length > 0) {
        setCartaoSelecionado(cartoesSalvos[0].id);
      } else {
        setCartaoSelecionado(null);
      }

    } catch (error) {
      console.error(
        "Erro ao carregar cartões:",
        error
      );

    } finally {
      setLoading(false);
    }
  };

  /* =========================================
     CARREGAR AO ABRIR
  ========================================= */

  useEffect(() => {
    carregarCartao();
  }, []);

  /* =========================================
     EXCLUIR CARTÃO
  ========================================= */

  const handleExcluir = async (id: string) => {
    try {
      await excluirCartao(id);

      if (cartaoSelecionado === id) {
        setCartaoSelecionado(null);
      }

      await carregarCartao();

    } catch (error) {
      console.error(
        "Erro ao excluir cartão:",
        error
      );
    }
  };

  /* =========================================
     DEFINIR CARTÃO PRINCIPAL
  ========================================= */

  const handleDefinirPrincipal = async (
    id: string
  ) => {
    try {
      await definirCartaoPadrao(id);

      await carregarCartao();

    } catch (error) {
      console.error(
        "Erro ao definir cartão principal:",
        error
      );
    }
  };

  /* =========================================
     LOADING
  ========================================= */

  if (loading) {
    return <p>Carregando...</p>;
  }

  /* =========================================
     RENDER
  ========================================= */

  return (
    <PageContainer title="Método de pagamento" icon={<Wallet size={18} />} showDesktopSearch={false} 
                   showLogo logo={ <img src="/imagens/menuLogoBranco.png" alt="Move"/>}>

      <div className={styles.page}>

        {/* =====================================
            CARTÕES SALVOS
        ===================================== */}

        {!openForm && cartoes.length > 0 && (

          <div className={styles.comCartao}>

            {/* =================================
                CABEÇALHO
            ================================= */}

            <div className={styles.cabecalhoCartoes}>

              <div>

                <p className={styles.titulo}>
                  Métodos salvos
                </p>

                <span
                  className={
                    styles.subTituloCartoes
                  }
                >
                  Selecione o cartão que deseja
                  utilizar
                </span>

              </div>

              <span
                className={
                  styles.quantidadeCartoes
                }
              >
                {cartoes.length}{" "}
                {cartoes.length === 1
                  ? "cartão"
                  : "cartões"}
              </span>

            </div>


            {/* =================================
                LISTA DE CARTÕES
            ================================= */}

            <div className={styles.listaCartoes}>

              {cartoes.map((cartao) => (

                <MetodoItem
                  key={cartao.id}
                  cartao={cartao}

                  selecionado={
                    cartaoSelecionado ===
                    cartao.id
                  }

                  onClick={() =>
                    setCartaoSelecionado(
                      cartao.id
                    )
                  }

                  onExcluir={
                    handleExcluir
                  }

                  onDefinirPrincipal={
                    handleDefinirPrincipal
                  }
                />

              ))}

            </div>


            {/* =================================
                ADICIONAR CARTÃO
            ================================= */}

            <button
              type="button"
              className={
                styles.btnAdicionarCartao
              }
              onClick={() =>
                setOpenForm(true)
              }
            >
              <Plus size={18} />

              <span>
                Adicionar novo cartão
              </span>
            </button>

          </div>
        )}


        {/* =====================================
            NENHUM CARTÃO
        ===================================== */}

        {!openForm &&
          cartoes.length === 0 && (

            <div className={styles.semConta}>

              <div
                className={
                  styles.containerImg
                }
              >
                <img
                  src="/imagens/cartoesVazio.png"
                  alt="Nenhum cartão"
                />
              </div>

              <p
                className={
                  styles.subTitulo
                }
              >
                Nenhum método de pagamento
                cadastrado
              </p>

              <button
                type="button"
                className={
                  styles.btnAdicionar
                }
                onClick={() =>
                  setOpenForm(true)
                }
              >
                Adicionar cartão
              </button>

            </div>
          )}


        {/* =====================================
            FORMULÁRIO
        ===================================== */}

        {openForm && (

          <CdCartao
            cartao={null}

            onClose={async () => {

              setOpenForm(false);

              await carregarCartao();

              if (returnTo) {

                navigate(returnTo, {
                  state: {
                    abrirCartoes: true,
                  },
                });

              }

            }}
          />

        )}

      </div>

    </PageContainer>
  );
};